import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ExecutionProviderBadge from '../components/ExecutionProviderBadge';
import bakingApi from '../services/bakingApi';

function ProjectsListPage() {
  const [projects, setProjects] = useState([]);
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('unity');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bakingApi.listProjects();
      setProjects(data);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const body = { name: name.trim(), execution_provider: provider };
      if (provider === 'maya') {
        body.application_version = '2024';
        body.unity_version = '';
        body.render_pipeline = '';
        body.render_pipeline_version = '';
      }
      await bakingApi.createProject(body);
      setName('');
      setProvider('unity');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <h1 className="product-page-title">Projects</h1>
      <p className="product-page-subtitle">
        Unity projects default to 2022.3.22f1 / HDRP 14.0.10. Maya projects support
        static archive upload only (execution is not enabled).
      </p>

      <div className="product-card">
        <Form className="row g-2 align-items-end" onSubmit={onCreate}>
          <div className="col-md-5">
            <Form.Label>New project name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
              required
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Provider</Form.Label>
            <Form.Select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              data-testid="project-provider-select"
            >
              <option value="unity">Unity</option>
              <option value="maya">Maya</option>
            </Form.Select>
          </div>
          <div className="col-md-4">
            <Button type="submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create project'}
            </Button>
          </div>
        </Form>
      </div>

      {error && <div className="error-state mb-3">{error}</div>}

      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading projects…
        </div>
      )}

      {!loading && projects.length === 0 && (
        <div className="product-card empty-state">No projects yet.</div>
      )}

      {!loading && projects.length > 0 && (
        <div className="product-card p-0">
          <Table responsive hover className="product-table mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Unity / App</th>
                <th>Pipeline</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td>
                    <Link to={`/console/projects/${project.id}`}>{project.name}</Link>
                  </td>
                  <td>
                    <ExecutionProviderBadge
                      provider={project.execution_provider || 'unity'}
                    />
                  </td>
                  <td>
                    {project.execution_provider === 'maya'
                      ? project.application_version || '—'
                      : project.unity_version}
                  </td>
                  <td>
                    {project.execution_provider === 'maya'
                      ? '—'
                      : `${project.render_pipeline} ${project.render_pipeline_version}`}
                  </td>
                  <td>{new Date(project.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Alert variant="light" className="mt-3 border">
        Project archive upload is not implemented. Use Django admin to create a development
        fixture revision when you need a ready revision for job submission.
      </Alert>
    </div>
  );
}

export default ProjectsListPage;
